/**
 * React Query hooks for deployment history data.
 * Provides paginated access to deployment records and lazy-loaded deployment detail (log).
 */

import { useQuery } from '@tanstack/react-query';

import type { DeploymentDetail, PagedDeployments } from '@/api/deploy';
import { getDeploymentDetail, listDeployments } from '@/api/deploy';
import { queryKeys } from '@/modules/query/query-keys';

// --- Types ---

export interface UseDeploymentHistoryParams {
  projectKey: string;
  page: number; // 0-indexed
  status?: 'RUNNING' | 'SUCCESS' | 'FAILED' | 'QUEUED';
  excludeTypes?: string;
  excludeRunIdPrefixes?: string;
}

// --- Hooks ---

/**
 * Fetches a paginated list of deployments for a project.
 * Supports optional status filtering and type/runId-prefix exclusion.
 * Page is 0-indexed (matching the backend).
 */
export function useDeploymentHistory(params: UseDeploymentHistoryParams) {
  const { projectKey, page, status, excludeTypes, excludeRunIdPrefixes } = params;

  return useQuery<PagedDeployments>({
    queryKey: queryKeys.deployments.list(projectKey, page, status, excludeTypes, excludeRunIdPrefixes),
    queryFn: () =>
      listDeployments({
        projectKey,
        page,
        size: 25,
        status,
        excludeTypes,
        excludeRunIdPrefixes,
      }),
    enabled: !!projectKey,
    retry: 1,
  });
}

/**
 * Fetches deployment detail (including log) for a single run.
 * Only enabled when runId is non-null (i.e., a row is expanded).
 */
export function useDeploymentDetail(runId: string | null) {
  return useQuery<DeploymentDetail>({
    queryKey: queryKeys.deployments.detail(runId ?? ''),
    queryFn: () => getDeploymentDetail(runId!),
    enabled: runId !== null,
    retry: 1,
  });
}
